// ── Collateral trend charts — one frame for every time-series chart ──────────
// Used by the Borrower Dashboard and the single-certificate page so both look
// and behave the same: each chart fills its card's measured width with the
// newest ST_VISIBLE periods, older ones scroll in from the left, and it opens
// scrolled to the newest. The y-axis is a separate SVG so it stays put while
// the bars scroll.
//
// Vertical geometry is fixed (ST.H / padT / padB), so a caller builds its y()
// with stY(min, max) and draws each period in renderItem(i, cx, slot). Anything
// that spans periods (a line) goes in renderOverlay(cxOf, slot).
import { useState, useEffect, useRef } from "react";
import { fmt, fmtDate } from "../../shared/format";

export const ST_VISIBLE = 24;
export const ST = { H: 230, padL: 52, padR: 10, padT: 12, padB: 42, labelPad: 28 };

export const fmtShort = v => { const a = Math.abs(v); return (a >= 1e6 ? (v / 1e6).toFixed(1) + "M" : a >= 1e3 ? Math.round(v / 1e3) + "K" : String(Math.round(v))); };
export const monthLbl = d => { const s = String(d || "").slice(0, 10); const p = s.split("-"); return p.length >= 3 ? p[1] + "/" + p[2] + "/" + p[0].slice(2) : (p.length >= 2 ? p[1] + "/" + p[0].slice(2) : s); };

export const stY = (min, max) => {
  const plotH = ST.H - ST.padT - ST.padB, span = (max - min) || 1;
  return v => ST.padT + plotH - ((v - min) / span) * plotH;
};

export const ScrollTrend = ({ n, labels, ticks, y, renderItem, renderOverlay, zeroLine, fmtTick = fmtShort, unit = "certificates" }) => {
  const [boxW, setBoxW] = useState(0);
  const boxRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBoxW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Start at the newest periods once the width is known (and again if it changes).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [boxW, n]);

  const { H, padL, padR, padB, labelPad } = ST;
  const viewW = Math.max(0, boxW - padL);
  const slot = n ? Math.max(8, (viewW - labelPad - padR) / Math.min(n, ST_VISIBLE)) : 0;
  const plotW = labelPad + slot * n + padR;
  const cxOf = i => labelPad + slot * i + slot / 2;

  return <>
    <div ref={boxRef} style={{ display: "flex", width: "100%" }}>
      <svg width={padL} height={H} style={{ display: "block", flex: "0 0 auto" }}>
        {ticks.map((t, i) => (
          <text key={i} x={padL - 4} y={y(t) + 3} textAnchor="end" fontSize="12" fill="var(--color-text-secondary)">{fmtTick(t)}</text>
        ))}
      </svg>
      <div ref={scrollRef} style={{ overflowX: n > ST_VISIBLE ? "auto" : "hidden", flex: "1 1 auto", minWidth: 0 }}>
        {boxW > 0 && (
          <svg width={plotW} height={H} style={{ display: "block" }}>
            {ticks.map((t, i) => (
              <line key={i} x1={0} y1={y(t)} x2={plotW} y2={y(t)} stroke="var(--color-border-tertiary)" strokeWidth="1" />
            ))}
            {zeroLine && <line x1={0} y1={y(0)} x2={plotW} y2={y(0)} stroke="var(--color-text-secondary)" strokeWidth="1" strokeDasharray="3 3" />}
            {Array.from({ length: n }, (_, i) => {
              const cx = cxOf(i);
              return (
                <g key={i}>
                  {renderItem(i, cx, slot)}
                  <text x={cx} y={H - padB + 12} textAnchor="end" fontSize="11" fill="var(--color-text-secondary)" transform={`rotate(-30 ${cx} ${H - padB + 12})`}>{labels[i]}</text>
                </g>
              );
            })}
            {renderOverlay && renderOverlay(cxOf, slot)}
          </svg>
        )}
      </div>
    </div>
    {n > ST_VISIBLE && (
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
        Showing the latest {ST_VISIBLE} of {n}. Scroll the chart left for older {unit}.
      </div>
    )}
  </>;
};

// Nested bars per period: each series a bar of width slot × w × scale, centred,
// widest drawn first so narrower ones sit inside it ("of which"). rows must be
// oldest → newest. Series: { key, label, color, w, id? }.
export const BarTrend = ({ rows, series, scale = 1, rx = 2 }) => {
  const vals = rows.flatMap(r => series.map(s => parseFloat(r[s.key])).filter(v => isFinite(v)));
  const max = Math.max(1, ...vals, 0);
  const y = stY(0, max), y0 = y(0);
  const ordered = series.slice().sort((a, b) => (b.w || 0) - (a.w || 0));
  return <ScrollTrend n={rows.length} labels={rows.map(r => monthLbl(r.asofdate))} ticks={[max, max / 2, 0]} y={y}
    renderItem={(i, cx, slot) => ordered.map(s => {
      const r = rows[i], v = parseFloat(r[s.key]) || 0, bw = Math.max(3, slot * (s.w || 0.6) * scale);
      return <rect key={s.id || s.key} x={cx - bw / 2} y={y(v)} width={bw} height={Math.max(0, y0 - y(v))} rx={rx} fill={s.color}>
        <title>{`${s.label} · ${fmtDate(r.asofdate)}: ${fmt(v)}`}</title>
      </rect>;
    })} />;
};

// Same, for a history list that arrives newest-first (getBorrowingBases order).
export const PairTrend = ({ hist, series }) => <BarTrend rows={hist.slice().reverse()} series={series} />;

// Stacked bars per period from a list of buckets ({key,label,color}); value(r, b)
// reads one bucket. rows oldest → newest. A/R aging, A/P aging, category mix.
export const StackTrend = ({ rows, buckets, value }) => {
  const totals = rows.map(r => buckets.reduce((s, b) => s + Math.max(0, value(r, b)), 0));
  const max = Math.max(1, ...totals);
  const y = stY(0, max);
  return <ScrollTrend n={rows.length} labels={rows.map(r => monthLbl(r.asofdate))} ticks={[max, max / 2, 0]} y={y}
    renderItem={(i, cx, slot) => {
      const r = rows[i], bw = Math.max(6, slot * 0.6);
      let acc = 0;
      return buckets.map(b => {
        const raw = value(r, b), v = Math.max(0, raw);
        const yTop = y(acc + v), yBot = y(acc); acc += v;
        return v ? <rect key={b.key} x={cx - bw / 2} y={yTop} width={bw} height={Math.max(0, yBot - yTop - 1)} fill={b.color}>
          <title>{`${b.label} · ${fmtDate(r.asofdate)}: ${fmt(raw)}`}</title>
        </rect> : null;
      });
    }} />;
};

// A/R trend: nested A/R bars plus the Excess / (Deficit) line, which can go
// negative — the axis then spans below zero with a dashed zero line.
export const ArExcessTrend = ({ hist, series, showExcess }) => {
  const asc = hist.slice().reverse();
  const exVals = asc.map(r => parseFloat(r.excess_availability) || 0);
  const posMax = Math.max(1, ...asc.flatMap(r => series.map(s => parseFloat(r[s.key]) || 0)), ...(showExcess ? exVals : []));
  const negMin = showExcess ? Math.min(0, ...exVals) : 0;
  const y = stY(negMin, posMax), y0 = y(0);
  const ticks = negMin < 0 ? [posMax, 0, negMin] : [posMax, posMax / 2, 0];
  return <ScrollTrend n={asc.length} labels={asc.map(r => monthLbl(r.asofdate))} ticks={ticks} y={y} zeroLine={negMin < 0}
    renderItem={(i, cx, slot) => series.map(s => {
      const r = asc[i], v = parseFloat(r[s.key]) || 0, bw = Math.max(3, slot * s.w);
      return <rect key={s.id || s.key} x={cx - bw / 2} y={y(v)} width={bw} height={Math.max(0, y0 - y(v))} rx="2" fill={s.color}>
        <title>{`${s.label} · ${fmtDate(r.asofdate)}: ${fmt(v)}`}</title>
      </rect>;
    })}
    renderOverlay={cxOf => showExcess && <>
      <polyline points={exVals.map((v, i) => cxOf(i) + "," + y(v)).join(" ")} fill="none" stroke="#BA7517" strokeWidth="2" />
      {exVals.map((v, i) => (
        <circle key={i} cx={cxOf(i)} cy={y(v)} r="3.8" fill={v >= 0 ? "#0F6E56" : "#993C1D"} stroke="#fff" strokeWidth="1.2">
          <title>{`Excess / (Deficit) · ${fmtDate(asc[i].asofdate)}: ${fmt(v)}`}</title>
        </circle>
      ))}
    </>} />;
};

// Table header cell for the collateral tables: labels wrap, data cells don't.
// width "1%" makes each column only as wide as its data needs, so a long label
// breaks onto two or three lines instead of setting the column width.
export const TH_WRAP = { whiteSpace: "normal", width: "1%", minWidth: 64, lineHeight: 1.25, verticalAlign: "bottom", padding: "8px 10px" };
